import React, { useState, useMemo } from 'react';
import { Flex, Text, Icon, Autocomplete, TextField, Spinner } from '@scale/scaleui-radix';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import {
  useSearchProjects,
  useGetProjectLibraryEntriesByIds,
} from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';

interface ProjectsAutoCompleteProps {
  projects: string[];
  onProjectsChange: (projects: string[]) => void;
}

export const ProjectsAutoComplete: React.FC<ProjectsAutoCompleteProps> = ({
  projects,
  onProjectsChange,
}) => {
  const [searchText, setSearchText] = useState('');

  const { data: allProjects = [] } = useSearchProjects({
    searchText,
  });

  const { data: projectLibraryEntries, isFetching: isFetchingProjectLibraryEntries } =
    useGetProjectLibraryEntriesByIds(projects);

  const selectedProjects = useMemo(
    () =>
      projectLibraryEntries?.libraryEntries?.map(entry => ({
        id: entry.project.id,
        name: entry.project.name,
      })) ?? [],
    [projectLibraryEntries],
  );

  const formattedProjects = (allProjects || []).map(project => ({
    id: project.id,
    name: project.name,
  }));

  return (
    <Flex direction="column" gap="2">
      <Text size="2" weight="medium">
        What projects is this tag used for?
      </Text>
      <Autocomplete.Root
        multiple
        value={projects}
        onValueChange={(newValue: string[]) => {
          setSearchText('');
          onProjectsChange(newValue);
        }}
      >
        <Autocomplete.Content>
          <Autocomplete.TextField
            placeholder="Type to search for project names or ids"
            value={searchText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
          >
            <TextField.Slot side="left">
              <Icon icon={faSearch} label="Search" />
            </TextField.Slot>
            <Autocomplete.Values>
              {projects.length > 0 &&
                isFetchingProjectLibraryEntries &&
                projects
                  .filter(project => !selectedProjects.find(p => p.id === project))
                  .map(project => (
                    <Autocomplete.Value key={project} value={project}>
                      <Spinner /> ({project})
                    </Autocomplete.Value>
                  ))}
              {selectedProjects.map(project => (
                <Autocomplete.Value key={project.id} value={project.id}>
                  {project.name} ({project.id})
                </Autocomplete.Value>
              ))}
            </Autocomplete.Values>
            <Autocomplete.Reset />
          </Autocomplete.TextField>
          <Autocomplete.Items>
            {formattedProjects.map(project => (
              <Autocomplete.Item key={project.id} value={project.id}>
                {project.name} ({project.id})
              </Autocomplete.Item>
            ))}
          </Autocomplete.Items>
        </Autocomplete.Content>
      </Autocomplete.Root>
    </Flex>
  );
};
